import { Component, OnInit, AfterViewInit, Input } from '@angular/core';
import { NewRelicService } from '../../../../../../angular-app/projects/brokerage-eapp/src/app/services/newrelic.service';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { fadeAnimation } from '../animations';

import { QuestionBase } from 'src/app/models/question-base';
import { QuestionControlService } from '../services/question-control.service';
import { QuestionService } from '../services/question.service';
import { ProcessorService } from '../services/processor.service';
import { NavigationService } from '../services/navigation.service';

// models
import { optionsMap } from 'src/app/models/options-map';
import { Store } from '@ngrx/store';

// reactive
import { Subscription, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

// store
import { AppState } from '../app-reducer';

// utilities
import { cloneDeep, find, forEach, get, pickBy } from 'lodash';
import { CommonService } from '../services/common.service';

@Component({
  selector: 'disclosures',
  templateUrl: './disclosures.component.html',
  styles: [],
  animations: [fadeAnimation]
})
export class DisclosuresComponent implements OnInit, AfterViewInit {
  @Input() questions: QuestionBase<any>[] = [];
  disclosuresForm: FormGroup;
  sectionBlock: string;
  currentRoute: string;
  sectionData: any;
  optionsMap: any = optionsMap;
  routeSection: any;
  appState$: Observable<any>;
  appStateSubscription: Subscription;
  homeState: null;
  proposedInsuredName: any;
  disableForward: boolean = false;
  role: string;
  defaultValues: any = null;
  sectionMenuData: any;
  menuData: any;
  customerSigned: boolean;
  appId: string;

  constructor(
    private newRelicService: NewRelicService,
    public route: ActivatedRoute,
    private store: Store<AppState>,
    private qcs: QuestionControlService,
    private qs: QuestionService,
    private processorService: ProcessorService,
    private navigationService: NavigationService,
    private commonService: CommonService
  ) {
    // section data
    this.sectionData = route.snapshot.data;
    this.appState$ = this.store.select('sections');
    this.currentRoute = route.snapshot.routeConfig.path;
    this.sectionBlock = this.sectionData.blockName;

    // get questions from state
    this.appState$.pipe(
      first()
    ).subscribe((state: any) => {
      this.appId = state.appId; 
      this.commonService.logAppState(state);

      if (state.questions) {
        const section: any = find(state.questions, { Name: this.sectionBlock });

        this.questions = section.Questions;

        this.questions.forEach(question => {
          if (question.QuestionName == 'DisclosuresSubmitted') {
            question.value = 'true';
          }
        });

        // used to inject form with pre-defined default values
        this.defaultValues = this.qs.defaultValues(section, state.userRole);
      }

      this.proposedInsuredName = get(state, 'content.SECTIONAProposedInsured.Name', {});

      this.disableForward = get(state, 'userRole.name') === 'Agent' && get(state, 'userRole.customerSigned') === 0 ? true : false;

      this.role = get(state, 'userRole.name');

      state.content.menuData.forEach(block => {
        
        // Seed menu/validation object with current block's questions
        if (block.name === this.sectionBlock) {
          if (block.questions.length === 0) {
            this.questions.forEach((q, i) => {
              const questionStatus = {'name': q.QuestionName, 'valid': null, 'visible': false};
              block.questions.push(questionStatus);
            });
          }
          this.sectionMenuData = block;
        }
      });

      this.menuData = state.content.menuData;

      // Log successful state retrieval
      this.newRelicService.addPageAction('State Retrieved', { status: 'success', appId: this.appId });
    }, (error) => {
      console.log('error', error);
      this.newRelicService.noticeError(error, { appId: this.appId });
    });
  }

  ngOnInit() {
    this.newRelicService.addPageAction('DisclosuresComponent ngOnInit', { status: 'initialized', appId: this.appId });

    this.disclosuresForm = this.qcs.toFormGroup(this.questions);

    // clear validators for all questions
    forEach(this.disclosuresForm.controls, control => {
      control.clearValidators();
      control.updateValueAndValidity();
    });

    // populate form and menu/validity object for initial view
    this.questions.forEach(question => {
      const shown = this.showConditionalQuestion(question);
      find(this.sectionMenuData.questions, { 'name': question.QuestionName }).visible = shown;
    });

    if (this.role !== 'Agent') {
      this.sectionMenuData.valid = this.disclosuresForm.valid;
      this.navigationService.updateNav(cloneDeep(this.disclosuresForm.controls), this.sectionBlock);
    }
  }

  ngAfterViewInit() {

    // set load timestamp for payload submission
    this.processorService.setServedTime();
    this.newRelicService.addPageAction('DisclosuresComponent ngAfterViewInit', { status: 'view initialized', appId: this.appId });
  }

  /**
   * Check ConditionalClause then ParentQuestion ConditionalClause if available.
   * @param question - question object
   * @returns boolean
   */
  showConditionalQuestion(question) {
    // Populate QuestionTextAlt based on AlternativeQuestionText ConditionalClause
    if (question.QuestionVariations) {
      question.QuestionVariations.forEach(cond => {
        if (this.processorService.analyzeConditionalArray(cond.ConditionalClause, this.questions, this.disclosuresForm.value)) {
          question.QuestionTextAlt = cond.AlternativeQuestionText ? cond.AlternativeQuestionText : " ";
          return;
        }
      });
    }
    return this.processorService.showConditionalQuestion(question, this.questions, this.disclosuresForm.value);
  }

  submitBlock(form, direction: string) {
    try {
      const { controls } = form;

      // sets navigationService observable to hide menu when menu nav is clicked
      if (direction !== 'fwd' && direction !== 'back' && direction !== 'save') {
        this.navigationService.setHideMenu(true);
      }

      // update form with default values
      this.disclosuresForm.patchValue(this.defaultValues);
      this.disclosuresForm.markAllAsTouched();

      if (this.role !== 'Agent' && (direction === 'fwd' || direction === 'express/declarations')) {
        this.sectionMenuData.valid = this.disclosuresForm.valid;
      }

      this.changeBlock(controls, direction);

      // Log form submission
      this.newRelicService.addPageAction('Form Submitted', { status: 'success', direction, appId: this.appId });
    } catch (error) {
      console.log('error', error);
      this.newRelicService.noticeError(error, { appId: this.appId });
    }
  }

  changeBlock(controls, direction?: string) {
    try {
      // process data and move to next step
      if (this.role === 'Agent') {
        this.navigationService.takeMeTo(this.disclosuresForm.value, false, this.route.snapshot.routeConfig, direction);
      } else {
        // Updates the menuData object with question visibility and validity
        this.navigationService.updateNav(cloneDeep(controls), this.sectionBlock);
        this.navigationService.takeMeTo(this.disclosuresForm.value, true, this.route.snapshot.routeConfig, direction);
      }

      // Log block change
      this.newRelicService.addPageAction('Block Changed', { status: 'success', direction, appId: this.appId });
    } catch (error) {
      console.log('error', error);
      this.newRelicService.noticeError(error, { appId: this.appId });
    }
  }
}